import React, { useState } from "react";
import { Button, Form, Modal, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

import { logActivity } from "../helpers/activities";
import ActivityDocumentsField from "./ActivityDocumentsField";
import CompanySearch from "./CompanySearch";
import RichNoteField, { noteIsEmpty, noteValue } from "./RichNoteField";
import { saveActivityDocuments } from "./activityDocuments";

const NoteFormModal = (props) => {
  const { company: companyProp, objectId, objectType, onClose, onSaved } = props;
  const { t } = useTranslation(["components", "common", "tasks"]);
  const { user } = useSelector((state) => ({ user: state.Auth.user }));

  // Firma im Fenster statt Auswahlschritt davor — vorbelegt, aenderbar, Pflicht.
  const [company, setCompany] = useState(companyProp || null);
  // Nach einem Firmenwechsel faellt der Vorgang weg: die Anfrage gehoert der
  // vorigen Firma.
  const sameCompany = !companyProp?.id || company?.id === companyProp.id;

  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  // Zeitpunkt ist der des Anlegens. Einen Ansprechpartner traegt die Notiz
  // bewusst NICHT — auch nicht den im Feed vorgewaehlten: sie haengt an der Firma
  // (und ggf. am Objekt), sonst nichts. Titel und Text reichen.
  const [saving, setSaving] = useState(false);
  const [docs, setDocs] = useState([]);

  const save = async () => {
    if (noteIsEmpty(body) || !company?.id) return;
    setSaving(true);
    try {
      const compId = company?.id;
      // Erst die Dateien, dann der Datensatz: die Anhaenge stehen als Feld IN der
      // Aktivitaet (kein @hasMany), also muessen ihre IDs vor der Mutation feststehen.
      const documents = await saveActivityDocuments({
        docs,
        companyId: compId,
        country: user?.country,
      });

      const activity = await logActivity({
        userId: user?.id,
        fields: {
          activityType: "note",
          subject: subject.trim() || t("Note"),
          body: noteValue(body),
          occurredAt: new Date().toISOString(),
          ...(compId ? { companyID: compId } : {}),
          ...(objectId && sameCompany
            ? { objectID: objectId, objectType: objectType || "CustomerRequest" }
            : {}),
          ...(documents.length > 0 ? { documents } : {}),
        },
      });

      toast.success(t("Note saved."));
      if (typeof onSaved === "function") onSaved(activity);
      onClose();
    } catch (error) {
      console.error("Notiz konnte nicht gespeichert werden", error);
      toast.error(t("Could not save the note."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show onHide={onClose} centered size="lg" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title className="h5">{t("Add a note")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group className="mb-3">
          <Form.Label>
            {t("Company")} <span className="text-danger">*</span>
          </Form.Label>
          <CompanySearch
            type="customerOrLead"
            allowLeads
            defaultValue={company}
            placeholder={t("Search company...", { ns: "tasks" })}
            onSelect={(option) => {
              const picked = option?.data || (option?.value ? { id: option.value } : null);
              setCompany(
                picked?.id
                  ? { id: picked.id, name: picked.name || option?.label || "" }
                  : null
              );
            }}
            isClearable
            showClear
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>{t("Subject")}</Form.Label>
          <Form.Control
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder={t("Subject (optional)")}
            autoFocus
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>
            {t("Note")} <span className="text-danger">*</span>
          </Form.Label>
          <RichNoteField
            rows={5}
            value={body}
            onChange={setBody}
            placeholder={t("Write a note...")}
          />
        </Form.Group>

        <ActivityDocumentsField
          docs={docs}
          setDocs={setDocs}
          companyId={company?.id}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="primary"
          onClick={save}
          disabled={saving || noteIsEmpty(body) || !company?.id}
        >
          {saving ? (
            <>
              <Spinner animation="border" size="sm" className="me-1" />
              {t("Saving...")}
            </>
          ) : (
            t("Save note")
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default NoteFormModal;
